import WarningPopup from 'components/WarningPopup'
import React, { useState, useCallback, useEffect } from 'react'
import { useIdleTimer } from 'react-idle-timer'

function useIdleTimeout({ timeout, warningTime, onIdle, onLogout }) {
  // Use the passed values, only use defaults if undefined (not just falsy)
  const finalTimeout = timeout !== undefined && timeout !== null ? timeout : 15 * 60 * 1000 // default 15 minutes
  const finalWarningTime = warningTime !== undefined && warningTime !== null ? warningTime : 60 * 1000 // default 1 minute
  
  const [showWarning, setShowWarning] = useState(false)
  const [countdown, setCountdown] = useState(finalWarningTime / 1000) // countdown in seconds

  const logoutUser = useCallback(() => {
    setShowWarning(false)
    if (onLogout) onLogout()
  }, [onLogout])

  useEffect(() => {
    let interval
    if (showWarning && countdown > 0) {
      interval = setInterval(() => {
        setCountdown((count) => count - 1)
      }, 1000)
    } else if (countdown <= 0 && showWarning) {
      logoutUser()
    }
    return () => clearInterval(interval)
  }, [showWarning, countdown, logoutUser])

  const handleOnIdle = useCallback(() => {
    setShowWarning(true)
    setCountdown(finalWarningTime / 1000)
    if (onIdle) onIdle()
  }, [finalWarningTime, onIdle])

  const { reset } = useIdleTimer({
    timeout: finalTimeout,
    onIdle: handleOnIdle,
    debounce: 500
  })

  const confirmActivity = useCallback(() => {
    reset()
    setShowWarning(false)
    setCountdown(finalWarningTime / 1000) // Reset countdown as part of activity confirmation
  }, [reset, finalWarningTime])

  // Handle tab visibility and focus events to dismiss warning
  useEffect(() => {
    if (!showWarning) return // Only listen when warning is shown

    const handleVisibilityChange = () => {
      // If tab becomes visible, dismiss warning
      if (document.visibilityState === 'visible') {
        confirmActivity()
      }
    }

    const handleWindowFocus = () => {
      // If window gets focus, dismiss warning
      confirmActivity()
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)
    window.addEventListener('focus', handleWindowFocus)

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      window.removeEventListener('focus', handleWindowFocus)
    }
  }, [showWarning, confirmActivity])

  // Handle touch/click events anywhere on screen to dismiss warning
  useEffect(() => {
    if (!showWarning) return // Only listen when warning is shown

    const handleActivityEvent = () => {
      confirmActivity()
    }

    // Add global touch and click listeners
    document.addEventListener('touchstart', handleActivityEvent)
    document.addEventListener('touchend', handleActivityEvent)
    document.addEventListener('click', handleActivityEvent)

    return () => {
      document.removeEventListener('touchstart', handleActivityEvent)
      document.removeEventListener('touchend', handleActivityEvent)
      document.removeEventListener('click', handleActivityEvent)
    }
  }, [showWarning, confirmActivity])

  const WarningPopupComponent = () => {
    return <WarningPopup open={showWarning} countdown={countdown} onConfirm={confirmActivity} />
  }
  return { WarningPopup: WarningPopupComponent }
}
export default useIdleTimeout


